import { useGetOfficeLocationsQuery } from "@/graphql/generated/schema";
import { Dropdown } from "antd";
import { ChevronDown } from "lucide-react";
import { useContext, useEffect } from "react";
import { CMSModal } from "@/context";
import { useTranslation } from "react-i18next";

interface OfficeLocationProps {
  onChange?: (location: string | null) => void;
  disabled?: boolean;
}

const OfficeLocation = ({ onChange, disabled }: OfficeLocationProps) => {
  const { t } = useTranslation();
  const { data } = useGetOfficeLocationsQuery();
  const { selectedLocation, selectedLocationName, setSelectedLocation } = useContext(CMSModal);
  const locations = data?.getOfficeLocations ?? [];

  const handleSelect = (item: any) => {
    const id = item?.id ?? null;
    setSelectedLocation(id, item?.name ?? null);
    onChange?.(id);
  };

  useEffect(() => {
    if (disabled && locations.length > 0 && !selectedLocation) {
      const first = locations.find((l: any) => l?.id);
      if (first) handleSelect(first);
    }
  }, [disabled, locations, selectedLocation]);

  const selected = locations.find((l: any) => l?.id === selectedLocation);
  const displayName = selected?.name ?? selectedLocationName;

  const menuItems = [
    ...(disabled
      ? [] // ❌ remove "All Locations" completely
      : [
          {
            key: "all",
            label: (
              <span className="text-sm font-medium text-gray-500">
                {t("home.allLocations")}
              </span>
            ),
          },
        ]),

    ...(locations as { id: string; name: string }[]).map((loc) => ({
      key: loc.id,
      label: (
        <span className="text-sm font-medium text-gray-500">{loc.name}</span>
      ),
    })),
  ];

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: ({ key }) => {
          if (key === "all") {
            handleSelect(null);
          } else {
            const selectedLoc = locations.find((l: any) => l?.id === key);
            selectedLoc && handleSelect(selectedLoc);
          }
        },
      }}
      trigger={["click"]}
    >
      <span className="inline-flex items-center gap-1 cursor-pointer font-normal text-inherit hover:opacity-80 transition-opacity">
        {displayName ?? t("home.allLocations")}
        <ChevronDown className="w-4 h-4 opacity-60" />
      </span>
    </Dropdown>
  );
};

export default OfficeLocation;
